import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { interval, Subscription } from 'rxjs';
import { filter, skip } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { EventLogService } from 'src/app/shared/logging/event-log.service';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import { LogsEventosGatewayApi } from 'src/core/apis/logs.eventos';
import { TransportistaSessionStorage } from 'src/core/jwt/session.storage';
import { CorrelationContextService } from './correlation-context.service';
import { DeviceIdService } from './device-id.service';
import { SessionIdService } from './session-id.service';
import { resolveSistemaOperativo } from './sistema-operativo.util';
import { EventLogQueueStorage } from './event-log-queue.storage';
import { ensureBodySizeLimit } from './front-event-log.sanitize';
import { buildDirectEvent, mapModuleEventToApi } from './front-event-log.mapper';
import {
  ConnectivityState,
  FrontEventLogPartial,
  FrontEventLogRequest,
  FrontEventLogResponse,
  PendingEventLogRecord,
} from './front-event-log.types';
import { randomUuid } from './uuid.util';

const RETRY_DELAYS_MS = [5_000, 15_000, 30_000, 60_000, 120_000];
const MAX_ATTEMPTS = 5;
const FLUSH_INTERVAL_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class FrontEventLogService {
  private initialized = false;
  private flushing = false;
  private warnedNoKey = false;
  private sinkUnregister: (() => void) | null = null;
  private subs: Subscription[] = [];

  constructor(
    private readonly logsApi: LogsEventosGatewayApi,
    private readonly queue: EventLogQueueStorage,
    private readonly eventLog: EventLogService,
    private readonly deviceId: DeviceIdService,
    private readonly correlation: CorrelationContextService,
    private readonly sessionIdService: SessionIdService,
    private readonly session: TransportistaSessionStorage,
    private readonly offlineStorage: OfflineStorageService
  ) {}

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    if (!this.isEnabled()) {
      if (!this.warnedNoKey && environment.logsEnabled) {
        this.warnedNoKey = true;
        if (!environment.production) {
          console.warn('[FrontEventLog] logsAppKey vacía — envío al API desactivado.');
        }
      }
      return;
    }

    this.sinkUnregister = this.eventLog.registerSink((local) => {
      const mapped = mapModuleEventToApi(local);
      if (mapped) {
        this.emit(mapped);
      }
    });

    this.subs.push(
      this.offlineStorage.online$
        .pipe(skip(1), filter((online) => online))
        .subscribe(() => this.flushQueue())
    );

    this.subs.push(
      interval(FLUSH_INTERVAL_MS).subscribe(() => this.flushQueue())
    );

    this.flushQueue();
  }

  /** Emite un evento al API (o lo encola si falla la red). */
  emit(partial: FrontEventLogPartial): void {
    if (!this.isEnabled()) {
      return;
    }
    const body = this.buildRequest(partial);
    void this.sendOrQueue(body, 0);
  }

  /** Emite usando module/action del catálogo interno (auth, viajes, etc.). */
  emitCatalog(
    module: string,
    action: string,
    extra?: Partial<FrontEventLogPartial>
  ): void {
    if (!this.isEnabled()) {
      return;
    }
    const mapped = buildDirectEvent(module, action, {
      tipoEvento: '',
      nivel: 'INFORMATION',
      modulo: module.toUpperCase().replace(/-/g, '_'),
      accion: action.toUpperCase(),
      mensaje: extra?.mensaje ?? '',
      ...extra,
    });
    if (mapped && mapped.tipoEvento) {
      this.emit(mapped);
    }
  }

  async flushQueue(): Promise<void> {
    if (!this.isEnabled() || this.flushing) {
      return;
    }
    this.flushing = true;
    try {
      const pending = await this.queue.listPending();
      const now = Date.now();
      const ready = pending
        .filter((r) => !r.permanentFailure && r.nextRetryAt <= now)
        .sort((a, b) => a.createdAt - b.createdAt);

      for (const record of ready) {
        await this.sendQueued(record);
      }
    } finally {
      this.flushing = false;
    }
  }

  private isEnabled(): boolean {
    return environment.logsEnabled && !!environment.logsAppKey;
  }

  private buildRequest(partial: FrontEventLogPartial): FrontEventLogRequest {
    const usuario = this.resolveUsuarioId();
    const body: FrontEventLogRequest = {
      eventId: partial.eventId ?? randomUuid(),
      correlationId: partial.correlationId ?? this.correlation.resolveCorrelationId(),
      fechaEvento: partial.fechaEvento ?? new Date().toISOString(),
      tipoEvento: partial.tipoEvento,
      nivel: partial.nivel,
      modulo: partial.modulo,
      accion: partial.accion,
      mensaje: partial.mensaje,
      usuarioId: partial.usuarioId ?? usuario ?? undefined,
      dispositivoId: partial.dispositivoId ?? this.deviceId.getDeviceId(),
      ticket: partial.ticket,
      pedido: partial.pedido,
      viajeId: partial.viajeId,
      entidad: partial.entidad,
      entidadId: partial.entidadId,
      detalleTecnico: partial.detalleTecnico,
      estadoConectividad:
        partial.estadoConectividad ?? this.resolveConnectivity(),
      versionAplicacion: partial.versionAplicacion ?? environment.appVersion,
      sistemaOperativo: partial.sistemaOperativo ?? resolveSistemaOperativo(),
      userAgent: partial.userAgent ?? navigator.userAgent,
      sessionId: partial.sessionId ?? this.sessionIdService.getSessionId() ?? undefined,
      rolUsuario: partial.rolUsuario ?? this.resolveRolUsuario() ?? undefined,
      endpointOrigen: partial.endpointOrigen ?? partial.endpointDestino,
      endpointDestino: partial.endpointDestino,
      codigoHttp: partial.codigoHttp,
      codigoError: partial.codigoError,
      cantidadIntentos: partial.cantidadIntentos ?? 0,
      datosAdicionales: partial.datosAdicionales,
    };
    return ensureBodySizeLimit(body);
  }

  private async sendOrQueue(body: FrontEventLogRequest, attempts: number): Promise<void> {
    try {
      const result = await this.logsApi.postEvento(body).toPromise();
      if (result) {
        await this.handleSuccess(body.eventId, result);
      }
    } catch (err) {
      await this.handleFailure(body, attempts, err);
    }
  }

  private async sendQueued(record: PendingEventLogRecord): Promise<void> {
    const body: FrontEventLogRequest = {
      ...record.body,
      cantidadIntentos: record.attempts,
    };
    try {
      const result = await this.logsApi.postEvento(body).toPromise();
      if (result) {
        await this.handleSuccess(record.eventId, result);
      }
    } catch (err) {
      await this.handleFailure(body, record.attempts, err, record.createdAt);
    }
  }

  private async handleSuccess(
    eventId: string,
    result: FrontEventLogResponse
  ): Promise<void> {
    if (
      result.code === 'EVENT_REGISTERED' ||
      result.code === 'EVENT_ALREADY_REGISTERED'
    ) {
      await this.queue.remove(eventId);
    }
  }

  private async handleFailure(
    body: FrontEventLogRequest,
    attempts: number,
    err: unknown,
    createdAt?: number
  ): Promise<void> {
    const httpErr = err instanceof HttpErrorResponse ? err : null;
    const code = this.extractErrorCode(httpErr, err);
    const status = httpErr?.status;

    if (code === 'INVALID_APP_KEY' || code === 'APP_KEY_REQUIRED' || status === 401) {
      await this.queue.enqueue({
        eventId: body.eventId,
        body,
        attempts: attempts + 1,
        nextRetryAt: Number.MAX_SAFE_INTEGER,
        createdAt: createdAt ?? Date.now(),
        permanentFailure: true,
      });
      return;
    }

    if (code === 'VALIDATION_ERROR' || status === 400) {
      await this.queue.enqueue({
        eventId: body.eventId,
        body,
        attempts: attempts + 1,
        nextRetryAt: Number.MAX_SAFE_INTEGER,
        createdAt: createdAt ?? Date.now(),
        permanentFailure: true,
      });
      return;
    }

    const nextAttempts = attempts + 1;
    if (nextAttempts >= MAX_ATTEMPTS) {
      await this.queue.enqueue({
        eventId: body.eventId,
        body: { ...body, cantidadIntentos: nextAttempts },
        attempts: nextAttempts,
        nextRetryAt: Number.MAX_SAFE_INTEGER,
        createdAt: createdAt ?? Date.now(),
        permanentFailure: true,
      });
      return;
    }

    const delay =
      RETRY_DELAYS_MS[Math.min(nextAttempts - 1, RETRY_DELAYS_MS.length - 1)];
    const retryBody =
      code === 'BODY_TOO_LARGE' || status === 413
        ? ensureBodySizeLimit({
            ...body,
            detalleTecnico: body.detalleTecnico?.slice(0, 200),
            datosAdicionales: undefined,
          })
        : body;

    await this.queue.enqueue({
      eventId: retryBody.eventId,
      body: { ...retryBody, cantidadIntentos: nextAttempts },
      attempts: nextAttempts,
      nextRetryAt: Date.now() + delay,
      createdAt: createdAt ?? Date.now(),
    });
  }

  private extractErrorCode(
    httpErr: HttpErrorResponse | null,
    err: unknown
  ): string | undefined {
    const body = httpErr?.error;
    if (body && typeof body === 'object' && 'code' in body) {
      return String((body as { code: string }).code);
    }
    if (httpErr?.status === 0) {
      return 'NETWORK_ERROR';
    }
    if (httpErr?.status === 504) {
      return 'LEGACY_TIMEOUT';
    }
    if (httpErr?.status === 502) {
      return 'LEGACY_CONNECTION_ERROR';
    }
    if (err && typeof err === 'object' && 'message' in err) {
      return String((err as { message: string }).message);
    }
    return undefined;
  }

  private resolveUsuarioId(): string | null {
    try {
      const gw = this.session.getUser<{ RUT_M?: string; RUT_USUARIO?: string }>();
      if (gw?.RUT_M) {
        return String(gw.RUT_M);
      }
      if (gw?.RUT_USUARIO) {
        return String(gw.RUT_USUARIO);
      }
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        return u?.RUT_USUARIO ? String(u.RUT_USUARIO) : null;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  private resolveConnectivity(): ConnectivityState {
    if (!navigator.onLine) {
      return 'OFFLINE';
    }
    return this.offlineStorage.isOnline() ? 'ONLINE' : 'OFFLINE';
  }

  private resolveRolUsuario(): string | null {
    try {
      const gw = this.session.getUser<{ ROL?: string }>();
      if (gw?.ROL) {
        return String(gw.ROL);
      }
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        return u?.ROL ? String(u.ROL) : null;
      }
    } catch {
      /* ignore */
    }
    return null;
  }
}
